import { FC, useState } from 'react';

import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { Link } from 'react-router-dom';

import { DeliverModal } from '../components/DeliverModal';
import { ExtraPaymentTab } from '../components/ExtraPaymentTab';
import { PhoneHistoryModal } from '../components/PhoneHistoryModal';
import {
  RedemptionCouponTable,
  VerificationData,
} from '../components/RedemptionCouponTable';
import { RedemptionStatsBar } from '../components/RedemptionStatsBar';
import { ShareHistoryModal } from '../components/ShareHistoryModal';
import { SignatureViewModal } from '../components/SignatureViewModal';
import { Toast } from '../components/Toast';
import { TodayDeliveredTable } from '../components/TodayDeliveredTable';
import { VerificationViewModal } from '../components/VerificationViewModal';
import { useRedemptionPage } from '../hooks/useRedemptionPage';

export const GiftCouponRedemptionListPage: FC = function GiftCouponRedemptionListPage() {
  const {
    dashboardStatus,
    statistics,
    visibleRequested,
    extraPaymentPending,
    todayDelivered,
    filteredData,
    productOptions,
    productFilter,
    setProductFilter,
    searchText,
    setSearchText,
    selectedIds,
    isAllSelected,
    handleToggleAll,
    handleToggleSelect,
    setSelectedIds,
    overIssuedSelectedIds,
    deselectOverIssued,
    allowOverIssue,
    requestOverIssueOverride,
    deliveringId,
    isBulkProcessing,
    openDeliverModal,
    openBulkDeliverModal,
    deliverModal,
    setDeliverModal,
    handleConfirmDeliver,
    handleReject,
    handleUpdateRecipientPhone,
    historyModal,
    setHistoryModal,
    openHistoryModal,
    shareHistoryModal,
    setShareHistoryModal,
    openShareHistoryModal,
    showTodayDelivered,
    setShowTodayDelivered,
    handleBulkUpdateDeliveredCoupon,
    handlePayExtraPayment,
    handleFulfillFromExisting,
    handleCancelSpawned,
    getDonorCandidates,
    toastMessage,
    showToast,
    toastDurationMs,
    copyToClipboard,
  } = useRedemptionPage();

  const [signatureModalImage, setSignatureModalImage] = useState<Image | null>(null);
  const [verificationModal, setVerificationModal] = useState<VerificationData | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<'pending' | 'extraPayment'>('pending');

  if (dashboardStatus === 'loading' || dashboardStatus === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">발송 대기 쿠폰</h1>
            <p className="mt-1 text-sm text-gray-500">
              교환 신청된 쿠폰을 확인하고 기프티콘을 발송하세요.
            </p>
          </div>
          <Link
            to="/gift-seller/coupon-history"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            쿠폰 내역 검색
          </Link>
        </div>
      </div>

      {/* Stats */}
      <RedemptionStatsBar
        totalPending={visibleRequested.length}
        todayDeliveredCount={todayDelivered.length}
        productStats={productOptions}
        blacklist={statistics?.blacklist ?? []}
        onProductClick={(key) => setProductFilter(key)}
      />

      {/* 오늘 발송 내역 */}
      <TodayDeliveredTable
        todayDelivered={todayDelivered}
        showTodayDelivered={showTodayDelivered}
        setShowTodayDelivered={setShowTodayDelivered}
        copyToClipboard={copyToClipboard}
        showToast={showToast}
        onBulkUpdate={handleBulkUpdateDeliveredCoupon}
      />

      {/* Tabs */}
      <div className="border-b bg-white px-4">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('pending')}
            className={`-mb-px border-b-2 px-4 py-3 text-sm font-medium ${
              activeTab === 'pending'
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            발송대기 ({visibleRequested.length})
          </button>
          <button
            onClick={() => setActiveTab('extraPayment')}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-3 text-sm font-medium ${
              activeTab === 'extraPayment'
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            추가결제 대기
            {extraPaymentPending.length > 0 && (
              <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-violet-600 px-1.5 text-[11px] font-bold text-white">
                {extraPaymentPending.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'extraPayment' && (
        <div className="p-4">
          <ExtraPaymentTab
            items={extraPaymentPending}
            onPay={handlePayExtraPayment}
            onFulfill={handleFulfillFromExisting}
            onCancel={handleCancelSpawned}
            getDonors={getDonorCandidates}
          />
        </div>
      )}

      {activeTab === 'pending' && (
        <>
      {/* Filters */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="max-w-[260px] rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상품</option>
            {productOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label} ({opt.count})
              </option>
            ))}
          </select>

          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="쿠폰번호, 전화번호, 주문자번호, 상품명 검색"
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        {selectedIds.size > 0 && (
          <div
            className={`mt-3 flex flex-wrap items-center gap-3 rounded-lg px-4 py-3 ${
              overIssuedSelectedIds.size > 0
                ? 'bg-red-50 ring-1 ring-red-300'
                : 'bg-indigo-50'
            }`}
          >
            <span
              className={`text-sm font-medium ${
                overIssuedSelectedIds.size > 0 ? 'text-red-800' : 'text-indigo-700'
              }`}
            >
              {selectedIds.size}건 선택됨
            </span>

            {overIssuedSelectedIds.size > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white shadow-sm ring-2 ring-red-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3.5 w-3.5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                초과발송건이 {overIssuedSelectedIds.size}건 확인됨
              </span>
            )}

            <button
              onClick={openBulkDeliverModal}
              disabled={
                isBulkProcessing ||
                (overIssuedSelectedIds.size > 0 && !allowOverIssue)
              }
              title={
                overIssuedSelectedIds.size > 0 && !allowOverIssue
                  ? '초과발송 의심 건이 포함되어 일괄 발송할 수 없습니다.'
                  : overIssuedSelectedIds.size > 0 && allowOverIssue
                  ? '강제 발송 모드 — 초과발송 건도 함께 발송됩니다'
                  : undefined
              }
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                overIssuedSelectedIds.size > 0
                  ? allowOverIssue
                    ? 'bg-red-600 hover:bg-red-700 ring-2 ring-red-700'
                    : 'bg-red-400 hover:bg-red-400'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {isBulkProcessing
                ? '처리 중...'
                : overIssuedSelectedIds.size > 0
                ? allowOverIssue
                  ? '초과건 포함 강제 발송'
                  : '일괄 발송 불가'
                : '일괄 발송처리'}
            </button>

            {overIssuedSelectedIds.size > 0 && !allowOverIssue && (
              <>
                <button
                  onClick={deselectOverIssued}
                  className="rounded-lg border border-red-400 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  초과건 {overIssuedSelectedIds.size}개 선택 해제
                </button>
                <button
                  onClick={requestOverIssueOverride}
                  title="관리자 시크릿 키로 초과발송 강제 발송 잠금 해제"
                  className="rounded-lg border border-red-500 bg-white px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50"
                >
                  🔒 초과건 강제 발송 잠금 해제
                </button>
              </>
            )}

            {allowOverIssue && (
              <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white ring-2 ring-red-700">
                강제 발송 모드 (손실 주의)
              </span>
            )}

            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              전체 선택 해제
            </button>

            <button
              onClick={() => {
                const phones = filteredData
                  .filter((c) => selectedIds.has(c._id))
                  .map((c) => c.recipient?.phoneNumber)
                  .filter((p): p is string => !!p)
                  .map((p) => p.replace(/[^0-9]/g, ''))
                  .filter((p) => p.length > 0);
                if (phones.length === 0) {
                  showToast('복사할 번호가 없습니다.');
                  return;
                }
                copyToClipboard(phones.join('\n'));
              }}
              title={`선택된 수신자 번호 복사 (${selectedIds.size}건)`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              선택된 번호 복사 ({selectedIds.size})
            </button>

            {overIssuedSelectedIds.size > 0 && !allowOverIssue && (
              <p className="basis-full text-xs font-medium text-red-700">
                ! 구매 수량을 초과한 위험 쿠폰이 선택되어 있습니다. 해당 건을 선택
                해제하거나, 강제 발송 잠금을 해제해야 일괄 발송이 가능합니다.
              </p>
            )}

            {overIssuedSelectedIds.size > 0 && allowOverIssue && (
              <p className="basis-full text-xs font-medium text-red-700">
                ! 강제 발송 모드 — 구매 수량을 초과한 위험 쿠폰 {overIssuedSelectedIds.size}건도
                함께 발송됩니다. (admin 손실 발생 가능)
              </p>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="p-4">
        <RedemptionCouponTable
          filteredData={filteredData}
          selectedIds={selectedIds}
          deliveringId={deliveringId}
          productFilter={productFilter}
          onToggleAll={handleToggleAll}
          onToggleSelect={handleToggleSelect}
          onOpenDeliverModal={openDeliverModal}
          onOpenHistoryModal={openHistoryModal}
          onOpenShareHistoryModal={openShareHistoryModal}
          onOpenSignatureModal={setSignatureModalImage}
          onOpenVerificationModal={setVerificationModal}
          onUpdateRecipientPhone={handleUpdateRecipientPhone}
          isAllSelected={isAllSelected}
          copyToClipboard={copyToClipboard}
          showToast={showToast}
          allowOverIssue={allowOverIssue}
        />
      </div>
        </>
      )}

      {/* Modals */}
      {deliverModal && (
        <DeliverModal
          deliverModal={deliverModal}
          setDeliverModal={setDeliverModal}
          onConfirm={handleConfirmDeliver}
          onReject={handleReject}
        />
      )}

      {historyModal && (
        <PhoneHistoryModal
          phone={historyModal.phone}
          history={historyModal.history}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {shareHistoryModal && (
        <ShareHistoryModal
          fullCode={shareHistoryModal.fullCode}
          history={shareHistoryModal.history}
          onClose={() => setShareHistoryModal(null)}
        />
      )}

      {signatureModalImage && (
        <SignatureViewModal
          signatureImage={signatureModalImage}
          onClose={() => setSignatureModalImage(null)}
        />
      )}

      {verificationModal && (
        <VerificationViewModal
          verification={verificationModal}
          onClose={() => setVerificationModal(null)}
        />
      )}

      {/* Toast */}
      <Toast message={toastMessage} durationMs={toastDurationMs} />
    </div>
  );
};
